import React, { type Dispatch, type FC, Fragment, type SetStateAction } from 'react';
import type { TrackingProp } from 'react-tracking';
import BEMHelper from '@ynap/bem-helper';
import { SimpleAsyncButton } from '@ynap/button';
import type { FormValues } from '@ynap/form';
import { Form } from '@ynap/form';
import { Tooltip } from '@ynap/notification';
import { AddressFields, getFields } from './AddressFields';
import { gridModifier, collateErrors, useAddressValidateContext } from './utils';
import type { Messages, Config, CheckoutAnalytics } from './@types';

const bem = new BEMHelper('AddressForm');

export interface AddressFormProps {
    className?: string;
    initialValues?: { [key: string]: string } | FormValues;
    messages: Messages;
    config: Config;
    country: string;
    onSubmit?: (values: FormValues) => any;
    onCancel?: () => void;
    touched?: boolean;
    isCheckoutLayout?: boolean;
    formTitle?: string;
    loading?: boolean;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    reduxForm?: any;
    isAddressBook?: boolean;
    removeAddressFinder?: (...args: any[]) => any;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
    invalidFields?: string[];
}

export const AddressForm: FC<AddressFormProps> = (props) => {
    const {
        className,
        config,
        country,
        formTitle,
        initialValues,
        isAddressBook,
        isCheckoutLayout,
        loading,
        messages,
        onCancel,
        onSubmit,
        removeAddressFinder,
        setRemoveAddressFinder,
        touched,
    } = props;
    const { countries } = useAddressValidateContext();
    const { head } = config;
    const addressValidationOptions = head?.addressValidationOptions || {};
    const fieldsOrdered = !!head?.fieldsOrdered;
    const enableLoqateOn = head?.enableLoqateOn?.includes(country);

    const addressFinderEnabled = config.head.loqate?.enabled && enableLoqateOn;
    const fieldsWCS = getFields(countries, (initialValues?.country as string) || country, addressFinderEnabled);
    const stateField = fieldsWCS?.find((fieldWCS) => fieldWCS.wcsType?.name === 'state');
    const verifiedInitialValues: { [key: string]: string } | FormValues = { ...initialValues };

    // Sets the initial value of the state field to an empty string, and deletes the stateName, if the value saved in the database is invalid
    if (
        !!initialValues?.state &&
        !!stateField?.values?.length &&
        typeof stateField.values.find((thisVal) => initialValues.state === (typeof thisVal !== 'string' ? thisVal.value : thisVal)) === 'undefined'
    ) {
        verifiedInitialValues.state = '';
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !!verifiedInitialValues?.stateName && delete verifiedInitialValues.stateName;
    }

    // If onSubmit is not provided, just render the fields, don't wrap in a form.
    return !onSubmit ? (
        <div className={bem(undefined, gridModifier(!isAddressBook && !fieldsOrdered, country), className)}>
            {!isAddressBook && formTitle && <h3 className={bem('title')}>{formTitle}</h3>}
            <AddressFields
                {...props}
                addressValidationOptions={addressValidationOptions}
                enableLoqateOn={enableLoqateOn}
                setRemoveAddressFinder={setRemoveAddressFinder}
            />
        </div>
    ) : (
        <Form
            className={bem(undefined, gridModifier(!isAddressBook && !fieldsOrdered, country), className)}
            collateErrors={collateErrors}
            initialValues={verifiedInitialValues}
            messages={messages}
            name="address-form"
            onSubmit={onSubmit}
            touched={touched}
        >
            {(submitting: boolean, errors: Array<string>, setFormTouched: (touched: boolean) => void) => {
                const error = errors ? errors[0] : undefined;
                const isAdding = isCheckoutLayout && !initialValues;
                let submitLabel: string | undefined;
                let cancelLabel: string | undefined;

                if (!isAddressBook) {
                    submitLabel = isAdding ? messages.addressSelectionForm?.submit?.() : messages?.addressForm?.updateContinue?.();
                    cancelLabel = isAdding ? undefined : messages.addressForm?.cancel?.();
                }

                if (!submitLabel) {
                    submitLabel = messages?.addressForm?.save?.();
                }

                return (
                    <Fragment>
                        {!isAddressBook && formTitle && <h3 className={bem('title')}>{formTitle}</h3>}
                        <AddressFields
                            {...props}
                            addressValidationOptions={addressValidationOptions}
                            enableLoqateOn={enableLoqateOn}
                            initialValues={verifiedInitialValues}
                            setFormTouched={setFormTouched}
                            setRemoveAddressFinder={setRemoveAddressFinder}
                        />
                        <Tooltip
                            key={String(submitting) || error}
                            className={bem('button', isCheckoutLayout ? { isCheckoutLayout } : undefined)}
                            defaultOpen={!submitting && !!error}
                            message={error}
                            type="error"
                        >
                            <SimpleAsyncButton
                                className={bem('save', isCheckoutLayout ? { isCheckoutLayout } : undefined)}
                                copy={submitLabel}
                                loading={submitting || loading}
                                onClick={() => removeAddressFinder?.()}
                                type="primary"
                            />
                            {!!cancelLabel && (
                                <div className={bem('link')}>
                                    <span onClick={onCancel}>{cancelLabel}</span>
                                </div>
                            )}
                        </Tooltip>
                    </Fragment>
                );
            }}
        </Form>
    );
};

export interface PropsWithOptimizely extends AddressFormProps {
    isEnabled: boolean;
    variables?: {
        enabled?: boolean;
    };
}
